import React from "react";
import {
  Chart as ChartJS,
  ChartData,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-luxon";
import { Scatter } from "react-chartjs-2";
import {
  clone,
  friendlyWindUnitName,
  graphHeight,
  locationLabel,
  SC_BASE_PATH,
} from "../util/util";
import { LocationData, WindowSize } from "../datastructures";
import { strip_empty_label_plugin } from "./chartParts/Strip_empty_label_plugin";
import { optionsWindDir } from "./chartParts/OptionsWindDir";
import { optionsWindSpd } from "./chartParts/OptionsWindSpd";
import { labelCallback } from "./chartParts/LabelCallback";
import { useRemoteData } from "../util/useRemoteData";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

ChartJS.defaults.font.size = 12;

//TODO eenheden omrekenen

// switch ($unifiedUnit){
//   case 'wind':
//     //$options['scales']['yAxes'][0]['ticks']['min'] =0;
//     //$options['scales']['yAxes'][0]['ticks']['max'] =30;
//     $options['scales']['yAxes'][1]=array();
//     $options['scales']['yAxes'][0]['id']='spd';
//     $options['scales']['yAxes'][1]['id']='dir';
//     $options['scales']['yAxes'][1]['ticks']['min'] =0;
//     $options['scales']['yAxes'][1]['ticks']['max'] =360;
//     $options['scales']['yAxes'][1]['ticks']['stepSize']=45;
//     $options['scales']['yAxes'][1]['position']='right';
//     $options['scales']['yAxes'][1]['scaleLabel']=array();
//     $options['scales']['yAxes'][1]['scaleLabel']['labelString']='Graden';
//     $options['scales']['yAxes'][1]['scaleLabel']['display']=true;

//     $u=friendlyWindUnitName($windUnits);
//     $graphTitle='Wind';
//     break;

// case 'm/s':
//     //$options['scales']['yAxes'][0]['ticks']['min'] =0;
//     //$options['scales']['yAxes'][0]['ticks']['max'] =30;
//     $u=friendlyWindUnitName($windUnits);
//     $graphTitle='Wind';
//     break;
//   case 'deg':
//     $options['scales']['yAxes'][0]['ticks']['min'] =0;
//     $options['scales']['yAxes'][0]['ticks']['max'] =360;
//     $graphTitle='Windrichting';
//     break;
//   case 'C':
//     $options['scales']['yAxes'][0]['ticks']['beginAtZero']=false;
//     $u='Graden Celcius';
//     $graphTitle='Temperatuur';
//     break;
//   case 'hPa':
//     $lowVal=null;
//     $highVal=null;
//     foreach ($data as $r){
//       foreach ($r as $f){
//         if ($f<10000){ // De etime moet niet mee gerekend worden
//           $lowVal=minnotnull($lowVal, $f);
//           $highVal=maxnotnull($highVal, $f);
//         }
//       }
//     }
//     $midVal=ceil(($lowVal+$highVal)/2);
//     #echo "L $lowVal h $highVal m $midVal";
//     $u='Hectopascal';
//     $graphTitle='Luchtdruk';
//     $options['scales']['yAxes'][0]['ticks']['min'] =$midVal - 12;
//     $options['scales']['yAxes'][0]['ticks']['max'] =$midVal + 12;

//     break;

// function friendlyWindUnitName($windUnits){
//   switch ($windUnits){
//   case 'kts':
//     $u='Knopen';
//     break;
//   case 'kmh';
//     $u='Kilometer per uur';
//     break;
//   default:
//     $u='Meters per seconde';
//   }
//   return $u;
// }

// Options changes from old soarcast format:
// Move title and legend into plugins
// removed surrounding options

// Data changes from old soarcast format:
// No new date(...)
// multiply timestamps by 1000
// Removed         "yAxes": [  (and also for y)
// Changed "t" to"x"
// Add showLine: true,

// location and day specified in query
// unifiedUnit in params

interface SoarcastGraphProperties {
  location: LocationData;
  activityId?: number;
  unifiedUnit: string;
  windUnits?: string;
  day: string;
  duringDayOnly: boolean;
  windowSize: WindowSize;
  runsToShow: string;
}

interface GraphData {
  data: ChartData<"scatter", unknown[]>;
}

export const SoarcastGraph: React.FC<SoarcastGraphProperties> = ({
  location,
  activityId,
  unifiedUnit,
  windUnits,
  day,
  duringDayOnly,
  windowSize,
  runsToShow,
}) => {
  const activityUrlString = activityId ? "&activity_id=" + activityId : "";
  const duringDayOnlyString = duringDayOnly
    ? "&duringDayOnly=" + duringDayOnly
    : "";

  const locationLbl = locationLabel(location);
  const locationId = location.id;

  // Build the URL string
  const runsString = runsToShow ? "&runsToShow=" + runsToShow : "";
  const url =
    unifiedUnit && locationId && day && runsToShow
      ? SC_BASE_PATH +
        "/plotPerLocAndUnitReact.php?json&jsonOnly&unit=" +
        unifiedUnit +
        "&location=" +
        locationId +
        "&day=" +
        day +
        "&windUnits=" +
        windUnits +
        "&forecastToShow=harm" +
        activityUrlString +
        duringDayOnlyString +
        runsString
      : null;

  // Use the custom hook
  const graphData = useRemoteData<GraphData>(url, [
    locationId,
    unifiedUnit,
    windUnits,
    day,
    activityUrlString,
    duringDayOnlyString,
    runsToShow,
  ]);

  let options;

  switch (unifiedUnit) {
    case "deg":
      options = clone(optionsWindDir);
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.title.text = "Windrichting (graden)";
      options.plugins.title.text =
        "Windrichting (graden) " + locationLbl + " " + day;
      options.scales.y.suggestedMin = 0;
      options.scales.y.suggestedMax = 360;
      options.scales.y.ticks.stepSize = 45;
      options.plugins.tooltip.callbacks.label = labelCallback("graden");

      break;
    case "m/s":
      options = clone(optionsWindSpd);
      const friendlyUnit = windUnits ? friendlyWindUnitName(windUnits) : "";
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.suggestedMin = 0;
      options.scales.y.title.text = "Windsnelheid (" + friendlyUnit + ")";
      options.plugins.title.text =
        "Windsnelheid (" + friendlyUnit + ") " + locationLbl + " " + day;
      options.plugins.tooltip.callbacks.label = labelCallback(friendlyUnit);

      break;
    case "C":
      options = clone(optionsWindDir); //TODO heeft deze een eigen nodig?
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.title.text = "Temperatuur (Graden C.)";
      options.scales.y.suggestedMin = 0;
      options.scales.y.suggestedMax = 30;
      options.scales.y.ticks.stepSize = 10;
      options.scales.y.ticks = {
        stepSize: 5,
      };
      options.plugins.title.text =
        "Temperatuur (Graden C.) " + locationLbl + " " + day;
      options.plugins.tooltip.callbacks.label = labelCallback(" C.");

      break;
    case "hPa":
      options = clone(optionsWindDir); //TODO heeft deze een eigen nodig?
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.plugins.title.text =
        "Luchtdruk (hectopascal) " + locationLbl + " " + day;
      options.scales.y.title.text = "Luchtdruk (hectopascal)";
      //TODO: ticks.minval en maxval mediaan + en min 12
      options.scales.y.suggestedMin = 980;
      options.scales.y.suggestedMax = 1020;
      options.scales.y.ticks.stepSize = 5;
      options.plugins.tooltip.callbacks.label = labelCallback(" hPa");

      break;
    default:
      // By setting options to null, at least it will become apparent that we forgot a unified unit...
      options = null;
      break;
  }

  return (
    <>
      <br />
      {/* Portrait: {portrait ? "yep" : "Nope"}
      <br />
      graphHeight: {graphHeight}
      <br />
      windowSize:{windowSize.innerWidth + "x" + windowSize.innerHeight}
     */}
      <div style={{ height: graphHeight(windowSize), position: "relative" }}>
        {graphData != null && graphData.data != null ? (
          <Scatter options={options} data={graphData.data} />
        ) : (
          "Loading graph data"
        )}
      </div>
    </>
  );
};
